var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import * as React from 'react';
import { useControlledState } from '../hooks/useControlledState';
import { setField, getField, findNextItem, slotDive, findLeftSlot, findRightSlot, findUpSlot, findDownSlot, findMaster } from '../utils';
import { SchedulerOccurrenceDialog } from '../components/SchedulerOccurrenceDialog';
import { SchedulerForm } from '../components/SchedulerForm';
import { SchedulerRemoveDialog } from '../components/SchedulerRemoveDialog';
import { DATA_ACTION } from '../Scheduler';
import { clone, Keys } from '@progress/kendo-react-common';
import { SchedulerDrag } from '../views/common/SchedulerDrag';
import { SchedulerResize } from '../views/common/SchedulerResize';
import { SchedulerViewItem } from './SchedulerViewItem';
import { useEditable } from '../hooks';
import { useSchedulerViewItemsContext, useSchedulerViewSlotsContext, useSchedulerViewSelectedItemContext } from '../context';
import { useSchedulerGroupsContext, useSchedulerFieldsContext, useSchedulerDataContext } from '../context/SchedulerContext';
import { SchedulerEditItemContext } from '../context/SchedulerEditItemContext';
import { ITEM_SELECTION_ACTION } from '../components/BaseView';
/**
 * Represents the default `editItem` component rendered by the [KendoReact Scheduler component]({% slug overview_scheduler %}).
 *
 * This is a composite component of the [`SchedulerViewItem`]({% slug api_scheduler_schedulerviewitem %}), extending it to provide editing through drag, resize and external form.
 */
export var SchedulerEditItem = React.forwardRef(function (props, ref) {
    // tslint:enable:max-line-length
    var _ref = props._ref, onDataAction = props.onDataAction, ignoreIsAllDay = props.ignoreIsAllDay, propViewItem = props.viewItem, propForm = props.form, propFormItem = props.formItem, onFormItemChange = props.onFormItemChange, propDrag = props.drag, propDragItem = props.dragItem, onDragItemChange = props.onDragItemChange, propResize = props.resize, propResizeItem = props.resizeItem, onResizeItemChange = props.onResizeItemChange, propRemoveDialog = props.removeDialog, propRemoveItem = props.removeItem, onRemoveItemChange = props.onRemoveItemChange, propOccurrenceDialog = props.occurrenceDialog, propShowOccurrenceDialog = props.showOccurrenceDialog, onShowOccurrenceDialogChange = props.onShowOccurrenceDialogChange, propShowRemoveDialog = props.showRemoveDialog, onShowRemoveDialogChange = props.onShowRemoveDialogChange;
    var editItem = React.useRef(null);
    var item = React.useRef(null);
    React.useImperativeHandle(editItem, function () { return ({ props: props, element: item.current && item.current.element }); });
    React.useImperativeHandle(ref, function () { return editItem.current; });
    React.useImperativeHandle(_ref, function () { return editItem.current; });
    var ViewItem = propViewItem || defaultProps.viewItem;
    var Drag = propDrag || defaultProps.drag;
    var Resize = propResize || defaultProps.resize;
    var Form = propForm || defaultProps.form;
    var OccurrenceDialog = propOccurrenceDialog || defaultProps.occurrenceDialog;
    var RemoveDialog = propRemoveDialog || defaultProps.removeDialog;
    var editable = useEditable(props.editable);
    var groups = useSchedulerGroupsContext();
    var fields = useSchedulerFieldsContext();
    var data = useSchedulerDataContext()[0];
    var items = useSchedulerViewItemsContext()[0];
    var slots = useSchedulerViewSlotsContext()[0];
    var _a = useSchedulerViewSelectedItemContext(), selectedItem = _a[0], dispatchItemSelection = _a[1];
    var _b = React.useState(null), series = _b[0], setSeries = _b[1];
    var _c = useControlledState(null, propFormItem, onFormItemChange), formItem = _c[0], setFormItem = _c[1];
    var _d = useControlledState(null, propDragItem, onDragItemChange), dragItem = _d[0], setDragItem = _d[1];
    var _e = useControlledState(null, propResizeItem, onResizeItemChange), resizeItem = _e[0], setResizeItem = _e[1];
    var _f = useControlledState(null, propRemoveItem, onRemoveItemChange), removeItem = _f[0], setRemoveItem = _f[1];
    var _g = useControlledState(false, propShowRemoveDialog, onShowRemoveDialogChange), showRemoveDialog = _g[0], setShowRemoveDialog = _g[1];
    var _h = useControlledState(false, propShowOccurrenceDialog, onShowOccurrenceDialogChange), showOccurrenceDialog = _h[0], setShowOccurrenceDialog = _h[1];
    var offset = React.useRef(0);
    var initialXRef = React.useRef(0);
    var initialYRef = React.useRef(0);
    var oldSlot = React.useRef(null);
    var selected = Boolean(selectedItem && selectedItem.current === item.current);
    var handleFocus = React.useCallback(function (event) {
        if (editable.select) {
            dispatchItemSelection({
                type: ITEM_SELECTION_ACTION.select,
                item: item
            });
        }
        if (props.onFocus) {
            props.onFocus.call(undefined, event);
        }
    }, []);
    var handleDoubleClick = React.useCallback(function (event) {
        if (editable.edit) {
            setFormItem(props.dataItem, event);
            if (props.isRecurring) {
                setShowOccurrenceDialog(true, event);
            }
        }
        if (props.onDoubleClick) {
            props.onDoubleClick.call(undefined, event);
        }
    }, [
        setFormItem, props.dataItem,
        props.isRecurring,
        setShowOccurrenceDialog
    ]);
    var handleRemoveClick = React.useCallback(function (event) {
        if (editable.remove) {
            setRemoveItem(props.dataItem, event);
            if (props.isRecurring) {
                setShowOccurrenceDialog(true, event);
            }
            else {
                setShowRemoveDialog(true, event);
            }
        }
        if (props.onRemoveClick) {
            props.onRemoveClick.call(undefined, event);
        }
    }, [
        setRemoveItem, props.dataItem,
        props.isRecurring,
        setShowOccurrenceDialog,
        setShowRemoveDialog
    ]);
    var handlePress = React.useCallback(function (event) {
        if (editable.drag) {
            initialXRef.current = event.dragEvent.clientX;
            initialYRef.current = event.dragEvent.clientY;
            var slot = slotDive(event.dragEvent.clientX, event.dragEvent.clientY, 7);
            if (!slot) {
                return;
            }
            var dataItem = clone(props.dataItem);
            var slotStart = slot.getAttribute('data-slot-start');
            var itemStart = getField(dataItem, fields.start);
            if (slotStart === null) {
                return;
            }
            offset.current = Number(slotStart) - new Date(itemStart).getTime();
        }
        if (props.onPress) {
            props.onPress.call(undefined, event);
        }
    }, [
        props.dataItem,
        fields.start,
        offset
    ]);
    var handleDrag = React.useCallback(function (event) {
        if (editable.drag) {
            // Dead-zone
            if (Math.abs(initialXRef.current - event.dragEvent.clientX) < 10
                && Math.abs(initialYRef.current - event.dragEvent.clientY) < 10) {
                return;
            }
            var slot = slotDive(event.dragEvent.clientX, event.dragEvent.clientY, 7);
            if (!slot) {
                return;
            }
            if (slot === oldSlot.current) {
                return;
            }
            var dataItem_1 = clone(props.dataItem);
            var slotStart = slot.getAttribute('data-slot-start');
            var slotGroupIndex_1 = slot.getAttribute('data-slot-group');
            var slotIsAllDay = slot.getAttribute('data-slot-allday') === 'true';
            if (!props.ignoreIsAllDay && slotIsAllDay !== props.isAllDay) {
                return;
            }
            var itemStart = new Date(getField(dataItem_1, fields.start));
            var itemEnd = new Date(getField(dataItem_1, fields.end));
            var duration = itemEnd.getTime() - itemStart.getTime();
            var start = new Date(Number(slotStart) - Number(offset.current));
            var end = new Date(Number(slotStart) - Number(offset.current) + duration);
            var group = groups.find(function (g) { return g.index === Number(slotGroupIndex_1); });
            setField(dataItem_1, fields.start, start);
            setField(dataItem_1, fields.end, end);
            setField(dataItem_1, fields.recurrenceRule, null);
            if (group && groups.length > 1) {
                group.resources.forEach(function (resource) {
                    if (!resource.multiple) {
                        setField(dataItem_1, resource.field, resource[resource.valueField]);
                    }
                });
            }
            oldSlot.current = slot;
            if (dataItem_1) {
                setDragItem(dataItem_1, event);
            }
        }
        if (props.onDrag) {
            props.onDrag.call(undefined, event);
        }
    }, [
        oldSlot,
        props.dataItem,
        fields.start, fields.end,
        groups,
        oldSlot,
        initialXRef,
        initialYRef,
        setDragItem
    ]);
    var handleRelease = React.useCallback(function (event) {
        if (editable.drag) {
            if (dragItem) {
                if (props.isRecurring && !props.isException) {
                    setShowOccurrenceDialog(true, event);
                }
                else {
                    var newDragItem = dragItem;
                    setDragItem(null, event);
                    if (onDataAction) {
                        onDataAction.call(undefined, {
                            type: DATA_ACTION.update,
                            series: false,
                            dataItem: newDragItem
                        });
                    }
                }
            }
        }
        if (props.onRelease) {
            props.onRelease.call(undefined, event);
        }
    }, [
        editable,
        dragItem,
        props.isRecurring, props.isException,
        setShowOccurrenceDialog,
        onDataAction,
        setDragItem
    ]);
    var handleResizePress = React.useCallback(function (event) {
        if (editable.resize) {
            event.dragEvent.originalEvent.stopPropagation();
            event.dragEvent.originalEvent.preventDefault();
            initialXRef.current = event.dragEvent.clientX;
            initialYRef.current = event.dragEvent.clientY;
            var slot = slotDive(event.dragEvent.clientX, event.dragEvent.clientY, 7);
            if (!slot) {
                return;
            }
            var dataItem = clone(props.dataItem);
            var slotStart = slot.getAttribute('data-slot-start');
            var itemStart = getField(dataItem, fields.start);
            if (slotStart === null) {
                return;
            }
            offset.current = Number(slotStart) - new Date(itemStart).getTime();
        }
        if (props.onResizePress) {
            props.onResizePress.call(undefined, event);
        }
    }, [
        initialXRef, initialYRef,
        props.dataItem,
        fields.start,
        offset
    ]);
    var handleResizeStartDrag = React.useCallback(function (event) {
        if (editable.resize) {
            var slot = slotDive(event.dragEvent.clientX, event.dragEvent.clientY, 7);
            if (!slot) {
                return;
            }
            if (slot === oldSlot.current) {
                return;
            }
            var slotStart = slot.getAttribute('data-slot-start');
            var newStart = new Date(Number(slotStart));
            if (newStart >= props.end) {
                return;
            }
            var dataItem = clone(props.dataItem);
            setField(dataItem, fields.start, newStart);
            setField(dataItem, fields.recurrenceRule, null);
            oldSlot.current = slot;
            setResizeItem(dataItem, event);
        }
        if (props.onResizeStartDrag) {
            props.onResizeStartDrag.call(undefined, null);
        }
    }, [
        oldSlot,
        offset,
        props.dataItem,
        fields.start,
        setResizeItem
    ]);
    var handleResizeEndDrag = React.useCallback(function (event) {
        if (editable.resize) {
            var slot = slotDive(event.dragEvent.clientX, event.dragEvent.clientY, 7);
            if (!slot) {
                return;
            }
            if (slot === oldSlot.current) {
                return;
            }
            var slotEnd = slot.getAttribute('data-slot-end');
            var newEnd = new Date(Number(slotEnd));
            if (newEnd <= props.start) {
                return;
            }
            var dataItem = clone(props.dataItem);
            setField(dataItem, fields.end, newEnd);
            setField(dataItem, fields.recurrenceRule, null);
            oldSlot.current = slot;
            setResizeItem(dataItem, event);
        }
        if (props.onResizeEndDrag) {
            props.onResizeEndDrag.call(undefined, event);
        }
    }, [
        oldSlot,
        props.start,
        props.dataItem,
        fields.end,
        setResizeItem
    ]);
    var handleResizeRelease = React.useCallback(function (event) {
        if (editable.resize) {
            if (resizeItem) {
                if (props.isRecurring && !props.isException) {
                    setShowOccurrenceDialog(true, event);
                }
                else {
                    var newResizeItem = resizeItem;
                    setResizeItem(null, event);
                    if (onDataAction) {
                        onDataAction.call(undefined, {
                            type: DATA_ACTION.update,
                            series: false,
                            dataItem: newResizeItem
                        });
                    }
                }
            }
        }
        if (props.onResizeRelease) {
            props.onResizeRelease.call(undefined, event);
        }
    }, [
        resizeItem,
        props.isRecurring, props.isException,
        setShowOccurrenceDialog,
        onDataAction,
        setResizeItem
    ]);
    var handleFormSubmit = React.useCallback(function (event) {
        if (onDataAction) {
            onDataAction.call(undefined, {
                type: DATA_ACTION.update,
                series: series,
                dataItem: event.value
            });
        }
        setFormItem(null, event);
    }, [
        series,
        onDataAction,
        setFormItem
    ]);
    var handleCancel = React.useCallback(function (event) {
        setSeries(null);
        setRemoveItem(null, event);
        setFormItem(null, event);
        setDragItem(null, event);
        setResizeItem(null, event);
        setShowRemoveDialog(false, event);
        setShowOccurrenceDialog(false, event);
    }, [
        setSeries,
        setRemoveItem,
        setFormItem,
        setDragItem,
        setResizeItem,
        setShowRemoveDialog,
        setShowOccurrenceDialog
    ]);
    var handleOccurrenceClick = React.useCallback(function (event) {
        if (dragItem) {
            if (onDataAction) {
                var dataItem = clone(dragItem);
                setField(dataItem, fields.recurrenceRule, getField(props.dataItem, fields.recurrenceRule));
                onDataAction.call(undefined, {
                    type: DATA_ACTION.update,
                    series: false,
                    dataItem: dataItem
                });
            }
            setDragItem(null, event);
        }
        if (resizeItem) {
            if (onDataAction) {
                var dataItem = clone(dragItem);
                setField(dataItem, fields.recurrenceRule, getField(props.dataItem, fields.recurrenceRule));
                onDataAction.call(undefined, {
                    type: DATA_ACTION.update,
                    series: false,
                    dataItem: dataItem
                });
            }
            setResizeItem(null, event);
        }
        if (formItem) {
            setSeries(false);
            setFormItem(props.dataItem, event);
        }
        if (removeItem) {
            setSeries(false);
            setRemoveItem(props.dataItem, event);
            setShowRemoveDialog(true, event);
        }
        setShowOccurrenceDialog(false, event);
    }, [
        setSeries,
        onDataAction,
        props.dataItem,
        formItem,
        setFormItem,
        dragItem,
        setDragItem,
        resizeItem,
        setResizeItem,
        removeItem,
        setRemoveItem,
        setShowRemoveDialog
    ]);
    var handleSeriesClick = React.useCallback(function (event) {
        if (dragItem) {
            var dataItem = clone(dragItem);
            setField(dataItem, fields.start, getField(dragItem, fields.start));
            setField(dataItem, fields.end, getField(dragItem, fields.end));
            setField(dataItem, fields.recurrenceRule, getField(props.dataItem, fields.recurrenceRule));
            if (onDataAction) {
                onDataAction.call(undefined, {
                    type: DATA_ACTION.update,
                    series: true,
                    dataItem: dataItem
                });
            }
            setDragItem(null, event);
        }
        if (resizeItem) {
            var dataItem = clone(resizeItem);
            setField(dataItem, fields.start, getField(resizeItem, fields.start));
            setField(dataItem, fields.end, getField(resizeItem, fields.end));
            setField(dataItem, fields.recurrenceRule, getField(props.dataItem, fields.recurrenceRule));
            if (onDataAction) {
                onDataAction.call(undefined, {
                    type: DATA_ACTION.update,
                    series: true,
                    dataItem: dataItem
                });
            }
            setResizeItem(null, event);
        }
        if (formItem) {
            setSeries(true);
            var dataItem = clone(findMaster(formItem, fields, data));
            setFormItem(dataItem, event);
        }
        if (removeItem) {
            setSeries(true);
            var dataItem = clone(removeItem);
            setRemoveItem(dataItem, event);
            setShowRemoveDialog(true, event);
        }
        setShowOccurrenceDialog(false, event);
    }, [
        setSeries,
        fields,
        formItem,
        dragItem,
        resizeItem,
        removeItem
    ]);
    var handleKeyDown = React.useCallback(function (event) {
        var currentSlot = props.slots.length && props.slots[0]._ref.current;
        var nextSlot;
        switch (event.syntheticEvent.keyCode) {
            case Keys.enter:
                if (!editable.edit) {
                    return;
                }
                setFormItem(props.dataItem, event);
                if (props.isRecurring) {
                    setShowOccurrenceDialog(true, event);
                }
                break;
            case Keys.tab:
                if (!items.current) {
                    return;
                }
                var next = findNextItem(event.target, items.current, props.ignoreIsAllDay, event.syntheticEvent.shiftKey);
                if (next) {
                    if (next.element) {
                        event.syntheticEvent.preventDefault();
                        next.element.focus();
                    }
                }
                break;
            case Keys.left:
                if (currentSlot && slots.current) {
                    nextSlot = findLeftSlot(currentSlot, slots.current);
                }
                break;
            case Keys.right:
                if (currentSlot && slots.current) {
                    nextSlot = findRightSlot(currentSlot, slots.current);
                }
                break;
            case Keys.up:
                if (currentSlot && slots.current) {
                    nextSlot = findUpSlot(currentSlot, slots.current);
                }
                break;
            case Keys.down:
                if (currentSlot && slots.current) {
                    nextSlot = findDownSlot(currentSlot, slots.current);
                }
                break;
            default:
                break;
        }
        if (nextSlot) {
            if (nextSlot.element) {
                event.syntheticEvent.preventDefault();
                nextSlot.element.focus();
            }
        }
        if (props.onKeyDown) {
            props.onKeyDown.call(undefined, event);
        }
    }, [
        slots,
        props.slots,
        setFormItem, props.dataItem,
        props.isRecurring, setShowOccurrenceDialog,
        props.ignoreIsAllDay
    ]);
    var handleRemoveConfirm = React.useCallback(function (event) {
        if (onDataAction && removeItem) {
            onDataAction.call(undefined, {
                type: DATA_ACTION.remove,
                series: series,
                dataItem: removeItem
            });
        }
        setRemoveItem(null, event);
        setShowRemoveDialog(false, event);
    }, [
        series,
        editItem,
        removeItem,
        onDataAction,
        setRemoveItem,
        setShowRemoveDialog
    ]);
    var handleFormItemChange = React.useCallback(function (value, event) {
        if (!setFormItem) {
            return;
        }
        setFormItem(value, event);
    }, [setFormItem]);
    var handleDragItemChange = React.useCallback(function (value, event) {
        if (!setDragItem) {
            return;
        }
        setDragItem(value, event);
    }, [setDragItem]);
    var handleResizeItemChange = React.useCallback(function (value, event) {
        if (!setResizeItem) {
            return;
        }
        setResizeItem(value, event);
    }, [setResizeItem]);
    var handleRemoveItemChange = React.useCallback(function (value, event) {
        if (!setRemoveItem) {
            return;
        }
        setRemoveItem(value, event);
    }, [setRemoveItem]);
    var handleRemoveDialogChange = React.useCallback(function (value, event) {
        if (!setShowRemoveDialog) {
            return;
        }
        setShowRemoveDialog(value, event);
    }, [setShowRemoveDialog]);
    var handleOccurrenceDialogChange = React.useCallback(function (value, event) {
        if (!setShowOccurrenceDialog) {
            return;
        }
        setShowOccurrenceDialog(value, event);
    }, [setShowOccurrenceDialog]);
    return (React.createElement(SchedulerEditItemContext, { props: props, form: [formItem, handleFormItemChange], drag: [dragItem, handleDragItemChange], resize: [resizeItem, handleResizeItemChange], remove: [removeItem, handleRemoveItemChange], showRemoveDialog: [showRemoveDialog, handleRemoveDialogChange], showOccurrenceDialog: [showOccurrenceDialog, handleOccurrenceDialogChange] },
        React.createElement(ViewItem, __assign({}, props, { _ref: item, selected: selected, 
            // Keyboard
            onKeyDown: handleKeyDown, 
            // Focus
            onFocus: handleFocus, 
            // Mouse
            onDoubleClick: handleDoubleClick, onRemoveClick: handleRemoveClick, 
            // Drag
            onPress: handlePress, onDrag: handleDrag, onRelease: handleRelease, 
            // Resize
            onResizePress: handleResizePress, onResizeEndDrag: handleResizeEndDrag, onResizeStartDrag: handleResizeStartDrag, onResizeRelease: handleResizeRelease })),
        (dragItem && editable.drag) && (React.createElement(Drag, { ignoreIsAllDay: ignoreIsAllDay, dataItem: dragItem, vertical: props.vertical, viewItem: ViewItem, item: props.item })),
        (resizeItem && editable.resize) && (React.createElement(Resize, { format: props.format, ignoreIsAllDay: ignoreIsAllDay, dataItem: resizeItem, viewItem: props.viewItem, item: props.item, vertical: props.vertical })),
        (formItem && !showOccurrenceDialog && editable.edit) && (React.createElement(Form, { dataItem: formItem, onSubmit: handleFormSubmit, onClose: handleCancel, onCancel: handleCancel })),
        (showOccurrenceDialog) && (React.createElement(OccurrenceDialog, { dataItem: formItem || dragItem || resizeItem || removeItem, isRemove: removeItem !== null, onClose: handleCancel, onOccurrenceClick: handleOccurrenceClick, onSeriesClick: handleSeriesClick })),
        (showRemoveDialog && removeItem && editable.remove) && (React.createElement(RemoveDialog, { dataItem: removeItem, onClose: handleCancel, onCancel: handleCancel, onConfirm: handleRemoveConfirm }))));
});
var defaultProps = {
    viewItem: SchedulerViewItem,
    drag: SchedulerDrag,
    resize: SchedulerResize,
    form: SchedulerForm,
    occurrenceDialog: SchedulerOccurrenceDialog,
    removeDialog: SchedulerRemoveDialog
};
SchedulerEditItem.defaultProps = defaultProps;
SchedulerEditItem.displayName = 'KendoReactSchedulerEditItem';
//# sourceMappingURL=SchedulerEditItem.js.map